import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { getInspectionUrl, formCategories } from "@Helpers";
import { formCategoryObject } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/constants";
import { taskInspectionAndProgressFormsController } from "@Actions/Forms";

import axiosWrapper from "@AxiosWrapper";
import type {
  FormTypesControllerResponse,
  FormControllerResponse,
  FormFlowControllerResponse,
  TaskInspectionAndProgressFormsControllerRequest,
} from "@ServerTypes/forms/forms.schema";
import _ from "lodash";

const useCreateInspection = (props: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState<any>(null);
  const [description, setDescription] = useState("");
  const [selectedFormTypeId, setSelectedFormTypeId] = useState("");
  const [selectedForm, setSelectedForm] = useState<any>({});
  const [rememberUserSettings, setRememberUserSettings] = useState(false);
  const [templateDataSource, setTemplateDataSource] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(null);
  const [showCreateFlowModal, setShowCreateFlowModal] = useState(false);
  const [spaceId, setSpaceId] = useState(props?.spaceId || null);
  const [selectedFormFlow, setSelectedFormFlow] = useState<any>({});
  const [attachEntity, setAttachEntity] = useState<any>({ name: "budget" });
  const [formFlowOptions, setFormFlowOptions] = useState<any>({
    allOptions: [],
    filteredOptions: [],
  });
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isFormFlowLoading, setIsFormFlowLoading] = useState(false);
  const [formFlowFilter, setFormFlowFilter] = useState<string>("");

  const isRustomjeePortal = props.portalData?.frontendUrl?.includes("rustomjee") || false;
  const isMPMRCLPortal = ["mpmrcl.app.cube.ms", "cube-pmis.mpmrcl.in"].includes(props.portalData?.frontendUrl) || false;
  const progressUpdateEntityCondition =
    [formCategories.inspection, formCategories.bills].includes(props.resourceType) && !isRustomjeePortal;
  const inspectionEntityCondition = props.resourceType == formCategories.bills;
  const allowedAttachmentConstants = [formCategories.progressUpdates, formCategories.inspection];

  const contractDetails = useSelector((state: RootState) => state.contractDetails);

  const customModules = useSelector((state: RootState) => state.customModules);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  useEffect(() => {
    if (props.contractId) {
      setSpaceId(_.get(contractDetails, "space._id"));
    }
  }, [contractDetails, props.contractId]);

  useEffect(() => {
    const fetchFormFlows = async () => {
      if (selectedForm?._id && spaceId && props.projectId && !showCreateFlowModal) {
        setIsFormFlowLoading(true);
        try {
          const response = await axiosWrapper.post<FormFlowControllerResponse>(
            `${getBackendUrl()}/forms/formFlowController`,
            {
              value: { selectedFormId: selectedForm._id, spaceId, projectId: props.projectId },
            },
          );
          if (response.data?.result) {
            setFormFlowOptions({
              allOptions: response.data?.result,
              filteredOptions: response.data?.result,
            });
            if (response.data?.result.length === 1) {
              setSelectedFormFlow(response.data?.result[0] || {});
            } else {
              setSelectedFormFlow({});
            }
          }
        } catch (error) {
          console.error("Error fetching form flows:", error);
          Promise.reject(error);
        } finally {
          setIsFormFlowLoading(false);
        }
      }
    };
    fetchFormFlows();
  }, [selectedForm?._id, spaceId, props.projectId, showCreateFlowModal]);

  const handleCancelFormAttachment = () => {
    setTitle("");
    setDescription("");
    setDueDate(null);
    setSpaceId(props.spaceId);
    props.setShowFormFlowCreationModal({ status: false, category: "" });
  };
  const combineArrays = useCallback((typeArray: any[] | undefined, templateArray: any[] | undefined) => {
    const typeMap = new Map(
      (typeArray ?? []).map((type: any) => [
        type._id,
        {
          _id: type._id,
          isChild: false,
          name: type.name,
          parentId: null,
        },
      ]),
    );

    const combinedArray: any[] = [];

    (templateArray ?? []).forEach((template: any) => {
      const parentType: any = typeMap.get(template.type._id);

      typeMap.set(template.type._id, { ...(parentType || {}), hasChild: true });

      if (parentType) {
        combinedArray.push({
          ...template,
          isChild: true,
          typeName: parentType.name,
          parentId: parentType._id,
          hasChild: false,
        });
      }
    });

    const valuesArray = Array.from(typeMap.values());

    valuesArray?.forEach((type: any) => {
      combinedArray.push({ ...type, parentId: null });
    });

    return combinedArray.filter((data: any) => data.parentId || data.hasChild);
  }, []);
  const fetchTemplateDataSource = useCallback(async () => {
    setIsTemplateLoading(true);
    try {
      const templateTypes = await axiosWrapper.post<FormTypesControllerResponse>(
        `${getBackendUrl()}/forms/formTypesController`,
        {
          value: {
            params: {
              resourceType: props.resourceType,
            },
            resourceType: props.resourceType,
          },
        },
      );
      const templates = await axiosWrapper.post<FormControllerResponse>(`${getBackendUrl()}/forms/formController`, {
        value: {
          params: {
            resourceType: props.resourceType,
            isArchived: false,
            projectId: props.projectId,
          },
          projectId: props.projectId,
          resourceType: props.resourceType,
          isArchived: false,
        },
      });

      const finalArray = combineArrays(templateTypes.data.result, templates.data.result);
      setTemplateDataSource(finalArray);
    } catch (error) {
      console.error("Error fetching form template:", error);
      Promise.reject(error);
    } finally {
      setIsTemplateLoading(false);
    }
  }, [props.resourceType, props.projectId, combineArrays]);
  useEffect(() => {
    fetchTemplateDataSource();
  }, [fetchTemplateDataSource]);

  const handleFormFlowCreate = () => {
    setShowCreateFlowModal(true);
  };

  const handleCreateClick = async (formFlow = []) => {
    let validatedDueDate = dueDate;
    if (validatedDueDate) {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      if (new Date(validatedDueDate).getTime() < startOfToday.getTime()) {
        dispatch(
          showToastNotification({
            content: t("Due Date cannot be in the past"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        setDueDate(null);
        return;
      }
    }
    setShowLoader(true);
    let formFlowLevels;
    let formTemplateId;

    if (selectedFormFlow.formFlow) {
      formFlowLevels = selectedFormFlow.formFlow;
      formTemplateId = selectedFormFlow.selectedForm._id;
    }
    if (formFlow.length) {
      formFlowLevels = formFlow;
      formTemplateId = selectedForm._id;
    }
    const formData: any = {
      title,
      description,
      dueDate: validatedDueDate ?? null,
      selectedTemplate: formTemplateId,
      rememberUserSettings,
      resourceType: selectedForm.resourceType,
      spaceId,
      projectId: props.projectId,
      formFlow: formFlowLevels.map((level: any) => ({
        dataSource: level.dataSource,
        allowDynamicUserConfig: level.allowDynamicUserConfig,
        allowedUsersToApprove: level.allowedUsersToApprove,
        allowedUsersToRollBack: level.allowedUsersToRollBack,
        allowedUsersToReject: level.allowedUsersToReject,
        allowedUsersToSubmit: level.allowedUsersToSubmit,
        heading: level.heading,
        approveBtnTxt: level.approveBtnTxt,
        submitBtnTxt: level.submitBtnTxt,
        rejectBtnTxt: level.rejectBtnTxt,
        rollBackBtnTxt: level.rollBackBtnTxt,
        actionStatus: false,
        actionDetails: {},
      })),
    };
    if (selectedFormFlow?._id) {
      formData.selectedFormFlow = {
        name: selectedFormFlow.name,
        _id: selectedFormFlow._id,
      };
    }
    if (props.contractId) {
      formData.basedOnBudget = true;
      formData.basedOnTasks = true;
      formData.contractId = props.contractId;
      formData.contractVersionId = contractDetails.versionId;
      formData.parentContract = contractDetails.parentId;
      formData.allParentContracts = contractDetails.allParents ?? [];
      formData.allChildContracts = contractDetails.allChilds ?? [];
    }

    if (attachEntity[formCategories.progressUpdates]) {
      formData.attachedProgressUpdates = [...attachEntity[formCategories.progressUpdates]];
      formData.basedOnProgressUpdates = true;
      delete formData.basedOnBudget;
      delete formData.basedOnTasks;
    } else if (attachEntity[formCategories.inspection]) {
      formData.attachedInspections = [...attachEntity[formCategories.inspection]];
      formData.basedOnInspections = true;
      delete formData.basedOnBudget;
      delete formData.basedOnTasks;
    }
    const data: TaskInspectionAndProgressFormsControllerRequest = {
      action: "save",
      data: formData,
    };
    try {
      const res = (await taskInspectionAndProgressFormsController(data)) as any;
      if (res.data.status) {
        if (!props.mobile) {
          handleCancelFormAttachment();
          const urlObj = {
            resourceId: res.data.id,
            projectId: props.projectId,
            resourceType: selectedForm.resourceType,
            contractId: props.contractId,
          };
          const categoryObj = customModules.find((module: any) => module.resourceType == selectedForm.resourceType);
          const formHeader = categoryObj?.formHeader || formCategoryObject[selectedForm.resourceType]?.formHeader || "";
          dispatch(
            showToastNotification({
              content: t("{{formHeader}} created successfully", { formHeader: formHeader }) as TranslatedString,
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          const location = getInspectionUrl(urlObj);
          if (selectedForm.resourceType == formCategories.changeOrder && props.contractId) {
            dispatch(props.getContractDetails({ contractId: props.contractId }));
          }
          props.navigate(location);
        } else {
          props.createInspectionRef.current.dismiss();
          props.router.push("Forms", {
            projectId: props.projectId,
            resourceId: res.data.id,
          });
        }
      } else {
        dispatch(
          showToastNotification({
            content: res.data.errorMessage,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } finally {
      setShowLoader(false);
    }
  };

  function handleSelectTemplate(params) {
    if (params) {
      setSelectedForm(params);
      setTitle(`${params.name} - ${(params?.project?.noOfFormsOfThisTemplate ?? 0) + 1}`);
    } else {
      setSelectedForm({});
      setTitle("");
    }
  }

  function handleSetTitle(value: string) {
    setTitle(value.substring(0, 500));
  }
  function handleSetDescription(value: string) {
    setDescription(value.substring(0, 500));
  }

  const checkUserPartInFlow = (formFlow: any, filter: "anyLevel" | "firstLevel") => {
    const levels: any[] = Array.isArray(formFlow) ? formFlow : [];

    const isUserInLevel = (level: any) => {
      const userIds = [
        ...(level?.allowedUsersToSubmit || []),
        ...(level?.allowedUsersToReject || []),
        ...(level?.allowedUsersToRollBack || []),
        ...(level?.allowedUsersToApprove || []),
        ...(level?.dataSource || []).flatMap((source: any) => source?.users || []),
      ].map((user: any) => user?.id);
      return userIds.includes(userDetails._id);
    };

    if (filter === "firstLevel") {
      return isUserInLevel(levels[0]);
    }
    return levels.some(isUserInLevel);
  };

  const handleFlowFilterChange = (filter: "anyLevel" | "firstLevel") => {
    setFormFlowFilter(filter);
    setFormFlowOptions((prev) => {
      const newOptions = prev.allOptions.filter((option) => {
        return checkUserPartInFlow(option.formFlow, filter);
      });
      return {
        allOptions: prev.allOptions,
        filteredOptions: newOptions,
      };
    });
  };

  const handleClearFlowFilter = () => {
    setFormFlowFilter("");
    setFormFlowOptions((prev) => ({
      allOptions: prev.allOptions,
      filteredOptions: prev.allOptions,
    }));
  };

  return {
    title,
    setTitle,
    description,
    setDescription,
    setDueDate,
    dueDate,
    selectedFormTypeId,
    setSelectedFormTypeId,
    selectedForm,
    setSelectedForm,
    spaceId,
    setSpaceId,
    selectedFormFlow,
    setSelectedFormFlow,
    handleCancelFormAttachment,
    handleFormFlowCreate,
    attachEntity,
    setAttachEntity,
    handleCreateClick,
    progressUpdateEntityCondition,
    inspectionEntityCondition,
    isRustomjeePortal,
    isMPMRCLPortal,
    setRememberUserSettings,
    rememberUserSettings,
    allowedAttachmentConstants,
    templateDataSource,
    setShowPreview,
    showPreview,
    showCreateFlowModal,
    setShowCreateFlowModal,
    formFlowOptions,
    isTemplateLoading,
    isFormFlowLoading,
    handleSelectTemplate,
    handleSetDescription,
    handleSetTitle,
    showLoader,
    formFlowFilter,
    handleFlowFilterChange,
    handleClearFlowFilter,
  };
};
export default useCreateInspection;
